import { APITypes } from '../../../primary-record-types';
import React from 'react';
import { useHasPermission } from '../../v2BaseComponents/Permissions/hooks';
import { useCollaborators } from '../../../hooks/api/useCollaborators';
import useMedicalProfileOwner from '../../../hooks/api/useMedicalProfileOwner';
import { isUserCollaborator } from '../../../utils/collaboratorUtils';
import getName from '../../../utils/getName';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import { EmailRecipient } from './types';

export type RecipientsProviderOwnProps = {
    medicalProfileId: string;
    children: (args: { recipients: EmailRecipient[] }) => React.ReactNode;
};

export type RecipientsProviderProps = RecipientsProviderOwnProps;

const dedupeByEmail = (recipients: EmailRecipient[]): EmailRecipient[] => {
    const seen = new Set<string>();
    return recipients.filter((recipient) => {
        if (seen.has(recipient.email)) return false;
        seen.add(recipient.email);
        return true;
    });
};

const buildRecipients = (
    collaborators: APITypes.medicalProfileService.MedicalProfileCollaborator[],
    owner: APITypes.medicalProfileService.GetMedicalProfileOwnerResponse | null,
): EmailRecipient[] => {
    const collaboratorRecipients = collaborators.filter(isUserCollaborator).map<EmailRecipient>((collaborator) => ({
        email: collaborator.userEmail,
        name: getName(collaborator.user),
    }));

    // The profile organizer (owner) is a distinct role from collaborators and is never part of the
    // collaborator list, so it must be added explicitly. Dedupe by email is defensive against any
    // duplicate addresses in the combined list.
    const ownerRecipient: EmailRecipient[] = owner ? [{ email: owner.email, name: getName(owner) }] : [];

    return dedupeByEmail([...collaboratorRecipients, ...ownerRecipient]);
};

type WithCollaboratorsRecipientsProviderProps = RecipientsProviderProps & {
    owner: APITypes.medicalProfileService.GetMedicalProfileOwnerResponse | null;
    isLoadingOwner: boolean;
};

const WithCollaboratorsRecipientsProvider: React.FC<WithCollaboratorsRecipientsProviderProps> = (props) => {
    const { isLoadingCollaborators, collaborators } = useCollaborators({
        medicalProfileId: props.medicalProfileId,
    });

    const recipients = React.useMemo<EmailRecipient[]>(
        () => buildRecipients(collaborators, props.owner),
        [collaborators, props.owner],
    );

    return (
        <AnimateLoadedContent isLoading={isLoadingCollaborators || props.isLoadingOwner}>
            {() => props.children({ recipients })}
        </AnimateLoadedContent>
    );
};

const RecipientsProvider: React.FC<RecipientsProviderProps> = (props) => {
    // The profile organizer (owner) is available to anyone with profile access — the owner endpoint
    // enforces collaborator access, not the stricter `collaborators:view` permission — so it is
    // fetched unconditionally. Only the collaborator *list* is gated on `collaborators:view`.
    const { isLoading: isLoadingOwner, owner } = useMedicalProfileOwner({
        medicalProfileId: props.medicalProfileId,
    });
    const canViewCollaborators = useHasPermission(props.medicalProfileId, 'collaborators', 'view');

    if (!canViewCollaborators) {
        return (
            <AnimateLoadedContent isLoading={isLoadingOwner}>
                {() => props.children({ recipients: buildRecipients([], owner) })}
            </AnimateLoadedContent>
        );
    }

    return <WithCollaboratorsRecipientsProvider {...props} owner={owner} isLoadingOwner={isLoadingOwner} />;
};

export default React.memo(RecipientsProvider);
